import { PureComponent } from 'react';
import { EventRTM, RTMTableColumn, RTMTableContainerProps, RTMTableContainerState } from '../types/RTMTypes';
import RTMTableView from '../views/RTMTableView';
import { t } from 'i18next';
import moment from 'moment';
import { Popover, Whisper } from 'rsuite';
import RTMSelectedProductsView from '../views/RTMSelectedProductsView';
import _ from 'lodash';
import RTMTableMiniModalView from '../views/RTMTableMiniModalView';

class RTMTableContainer extends PureComponent<RTMTableContainerProps, RTMTableContainerState> {
  constructor(props: RTMTableContainerProps) {
    super(props);
    this.state = {
      showModal: false,
      selectedProduct: null,
    };
  }

  componentDidUpdate() {
    if (this.state.selectedProduct) {
      const updatedProduct = this.props.productsInTable.find(
        (product) => product.puuid === this.state.selectedProduct!.puuid
      );
      if (updatedProduct) {
        this.setState({ selectedProduct: updatedProduct });
      } else {
        this.setState({ selectedProduct: null, showModal: false });
      }
    }
  }

  handleViewMore = (puuid: string) => {
    const selectedProduct = this.props.productsInTable.find((product) => product.puuid === puuid);
    if (selectedProduct) {
      this.setState({
        showModal: true,
        selectedProduct,
      });
    }
  };

  handleCloseModal = () => {
    this.setState({
      showModal: false,
      selectedProduct: null,
    });
  };

  deviceTypeFormatter({ row }) {
    return (
      <div className='deviceType-container'>
        <div className='treeImage' style={{ backgroundImage: `url('${row.imageUri}')` }}>
          <div className={`dotState ${row.connectionState.toLowerCase()}`}></div>
        </div>
        <div className='deviceTypeName-container'>
          <h4 className='col-12 p-0 m-0 productType'>{row.name}</h4>
          <div className='m-0 TreeDetailProduct'>{row.puuid}</div>
        </div>
      </div>
    );
  }

  dataFormatter({ value }) {
    return moment(value).format('DD/MM/YYYY HH:mm');
  }

  isBold = (alarmType: string) => {
    const alarmTypeInBold = ['alarm', 'rule', 'alert'];
    return alarmType && alarmTypeInBold.includes(alarmType.toLowerCase()) ? 'bold' : '';
  };

  truncateText = (text: string, maxLength = 55) => {
    return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
  };

  renderDot = (alarm: EventRTM) => {
    return alarm.alarmType ? alarm.alarmType.toLowerCase(): alarm.type.toLowerCase();
  };

  eventDescriptionFormatter = ({ row }) => {
    const maxEventsToShowInTable = 3;
    const limitedEvents: EventRTM[] = row.alertsAndEvents.slice(0, maxEventsToShowInTable);
    const hasTruncatedEvent = limitedEvents.some((alert) => {
      return this.truncateText(alert.name).endsWith('...') || this.truncateText(alert.description).endsWith('...');
    });

    return (
      <div className='descriptions-column'>
        {limitedEvents.map((alert, index) => {
          const truncatedName = this.truncateText(alert.name);
          const truncatedDescription = this.truncateText(alert.description);
          return (
            <Whisper
              key={index}
              placement='auto'
              trigger='hover'
              speaker={<Popover>{moment(alert.timestamp).format('DD/MM/YYYY HH:mm')}</Popover>}
            >
              <div className='event'>
                <div className='event-box'>
                  <span className={`dot ${this.renderDot(alert)}`}></span>
                  <p className={`event-name truncate-text ${this.isBold(alert.alarmType)}`}>{truncatedName}</p>
                </div>
                <p className='event-description truncate-text'>{truncatedDescription}</p>
              </div>
            </Whisper>
          );
        })}

        {(row.alertsAndEvents.length > maxEventsToShowInTable || hasTruncatedEvent) && (
          <button className='view-more-button' onClick={() => this.handleViewMore(row.puuid)}>
            {t('viewmore')}
          </button>
        )}
      </div>
    );
  };

  columns(): RTMTableColumn[] {
    return [
      {
        dataField: 'deviceType',
        text: t('DeviceType'),
        align: 'left',
        sort: true,
        hidden: false,
        formatter: this.deviceTypeFormatter.bind(this),
        headerStyle: () => {
          return { width: '25%' };
        },
      },
      {
        dataField: 'status',
        text: t('Status'),
        align: 'left',
        sort: true,
        headerStyle: () => {
          return { width: '10%' };
        },
      },
      {
        dataField: 'lastStatusDate',
        text: t('Last status date'),
        align: 'left',
        sort: true,
        hidden: false,
        formatter: this.dataFormatter.bind(this),
        headerStyle: () => {
          return { width: '20%' };
        },
      },
      {
        dataField: 'lastEventDescription',
        text: t('Last event description'),
        align: 'left',
        sort: true,
        hidden: false,
        formatter: this.eventDescriptionFormatter.bind(this),
        headerStyle: () => {
          return { width: '25%' };
        },
      },
      {
        dataField: 'lastEventDate',
        text: t('Last event date'),
        align: 'left',
        sort: true,
        hidden: false,
        formatter: this.dataFormatter.bind(this),
        headerStyle: () => {
          return { width: '20%' };
        },
      },
    ];
  }

  render() {
    return (
      <>
        <div className='selected-products'>
          <RTMSelectedProductsView selectedProducts={this.props.selectedProducts} />
        </div>
        <div className='table-container'>
          <RTMTableView columns={this.columns()} rows={this.props.productsInTable} />
        </div>
        {this.state.selectedProduct && this.state.showModal && (
          <RTMTableMiniModalView
            showModal={this.state.showModal}
            selectedProduct={this.state.selectedProduct}
            handleCloseModal={this.handleCloseModal}
          />
        )}
      </>
    );
  }
}

export default RTMTableContainer;
